import React from 'react';
import { Link, useParams } from 'react-router-dom';
import { SiteHeader } from '@/components/SiteHeader';
import { ArrowLeft } from 'lucide-react';

const POLICIES = {
  privacy: {
    title: 'Privacy Policy',
    lastUpdated: 'March 26, 2026',
    content: `# Privacy Policy

**Effective Date:** March 26, 2026
**Last Updated:** March 26, 2026

Whisper.AI ("we", "our", "us") operates the website whisperai.one and the Whisper.AI platform. This Privacy Policy explains how we collect, use, disclose, and safeguard your information when you use our service.

## 1. Information We Collect

### Personal Information
When you create an account, we collect:
- Full name
- Email address
- Password (stored as a secure hash, never in plain text)
- Billing information (name, company, address, GST/Tax ID)

### Usage Data
We automatically collect:
- Chat message content between visitors and your AI bots
- Message volume and usage statistics per widget
- Pages crawled via the Website Crawl feature
- Login timestamps and IP addresses
- Browser type and device information via cookies

### Payment Data
Payment processing is handled by Razorpay. We do not store your credit card or bank details. Razorpay's privacy policy applies to payment transactions.

## 2. How We Use Your Information

We use collected information to:
- Provide and maintain the Whisper.AI platform
- Process your subscription payments
- Send verification emails and account notifications
- Track usage against your plan limits
- Improve our AI responses and platform features
- Provide customer support
- Send important service updates
- Comply with legal obligations

## 3. Knowledge Base & Chat Data

- Your Knowledge Base Q&A pairs are stored securely and used exclusively to train YOUR bots
- Knowledge Base data is never shared across accounts
- Chat conversations between visitors and your bots are stored for your Chat History feature
- Chat history retention is based on your subscription plan
- We do not use your chat data to train our models or share it with third parties

## 4. Data Sharing

We do NOT sell your personal information. We may share data with:
- **Razorpay** — for payment processing
- **OpenAI (GPT-4o-mini via Bytez API)** — chat messages are sent to generate AI responses. OpenAI's data usage policies apply.
- **Hosting providers** — our infrastructure partners for running the platform
- **Law enforcement** — if required by law or to protect our rights

## 5. Data Security

We implement industry-standard security measures:
- All data transmitted via HTTPS encryption
- Passwords are hashed using bcrypt
- JWT-based authentication with expiring tokens
- Each user account is fully isolated
- Database access is restricted and monitored

## 6. Data Retention

- Account data is retained as long as your account is active
- Chat history is retained based on your plan limits (10 to unlimited conversations)
- When you delete your account, all associated data (widgets, knowledge base, chat history, invoices) is permanently deleted
- You can delete individual conversations from Chat History at any time

## 7. Your Rights

You have the right to:
- Access your personal data
- Correct inaccurate data
- Delete your account and all associated data
- Export your data
- Opt out of marketing communications
- Withdraw consent for cookies

## 8. Children's Privacy

Whisper.AI is not intended for children under 16. We do not knowingly collect personal information from children.

## 9. International Data Transfers

Your data may be processed in countries outside your country of residence. We ensure appropriate safeguards are in place.

## 10. Changes to This Policy

We may update this Privacy Policy from time to time. We will notify you of significant changes via email or a notice on our website.

## 11. Contact Us

For privacy-related questions or requests:
- **Email:** support@whisperai.one
- **Website:** https://whisperai.one`,
  },

  cookies: {
    title: 'Cookie Policy',
    lastUpdated: 'March 26, 2026',
    content: `# Cookie Policy

**Effective Date:** March 26, 2026

This Cookie Policy explains how Whisper.AI uses cookies and similar technologies on whisperai.one.

## What Are Cookies?

Cookies are small text files stored on your device when you visit a website. They help the website remember your preferences and improve your experience.

## Cookies We Use

### Essential Cookies (Always Active)
These are necessary for the website to function properly:
- **Authentication token** — keeps you logged in to your dashboard (localStorage)
- **Theme preference** — remembers your dark/light mode choice (localStorage)
- **Cookie consent** — remembers your cookie preference (localStorage)

### Analytics Cookies (Optional)
These help us understand how visitors use our website:
- **Google Analytics (G-6BZ2HL98Y5)** — tracks page views, visitor count, traffic sources, and user behavior. Data is anonymized and aggregated.
  - Cookie names: _ga, _ga_*, _gid
  - Duration: Up to 2 years
  - Provider: Google LLC

### Third-Party Cookies
- **Cloudflare (__cf_bm)** — bot protection and security. Set automatically by our CDN.
  - Duration: 30 minutes
  - Provider: Cloudflare Inc.
- **Razorpay** — set during the payment checkout process only.
  - Provider: Razorpay Software Private Ltd.

## Embedded Widget Cookies

When your AI chat widget is embedded on third-party websites:
- The widget itself does NOT set cookies on the visitor's browser
- API calls use credentials: 'omit' to avoid cross-origin cookie issues
- No tracking cookies are placed on your website visitors through the widget

## Managing Cookies

### Accept or Decline
When you first visit whisperai.one, a cookie consent banner appears. You can:
- **Accept All** — enables all cookies including analytics
- **Decline** — disables analytics cookies. Essential cookies remain active as they are necessary for the site to function.

### Browser Settings
You can also control cookies through your browser settings:
- Chrome: Settings > Privacy and Security > Cookies
- Firefox: Settings > Privacy & Security > Cookies
- Safari: Preferences > Privacy > Cookies
- Edge: Settings > Cookies and site permissions

### Delete Cookies
You can delete all cookies at any time through your browser settings. Note that this will log you out of your Whisper.AI account.

## Changes to This Policy

We may update this Cookie Policy from time to time. Changes will be posted on this page.

## Contact Us

For questions about our cookie practices:
- **Email:** support@whisperai.one`,
  },

  refund: {
    title: 'Refund Policy',
    lastUpdated: 'March 26, 2026',
    content: `# Refund Policy

**Effective Date:** March 26, 2026

## No Refund Policy

Whisper.AI operates on a **strict no-refund policy** for all subscription plans.

## Key Terms

### Monthly Subscriptions
- All paid plans (Single, Starter, Pro, Business) are billed monthly on a recurring basis
- Payments are non-refundable once processed
- You will not receive a refund for any unused portion of your subscription period

### Cancellation
- You may cancel your subscription at any time from the Billing page in your dashboard
- When you cancel, your current plan features remain active until the **end of your current billing period**
- After the billing period ends, your account will be downgraded to the Free plan
- No refund is issued for the remaining days of the billing period

### Downgrading
- You may downgrade to a lower plan at any time
- The downgrade takes effect at the end of your current billing period
- No refund is issued for the price difference between your current and new plan
- You retain access to your current plan's features until the billing period ends

### Plan Changes
- Upgrading to a higher plan takes effect immediately upon successful payment
- The new plan's billing cycle starts from the date of upgrade
- No pro-rata credits are issued for the unused portion of your previous plan

## Free Plan

- The Free plan is available at no cost with no time limit
- No payment information is required for the Free plan
- We recommend trying the Free plan before committing to a paid subscription

## Exceptions

In rare circumstances, we may consider refund requests for:
- Technical issues on our end that prevented you from using the service for an extended period
- Duplicate charges due to a billing error

To request an exception, contact us within 7 days of the charge at support@whisperai.one with:
- Your account email
- The transaction/payment ID
- A description of the issue

All exception requests are reviewed on a case-by-case basis and are at our sole discretion.

## Chargebacks

If you initiate a chargeback or payment dispute instead of contacting us:
- Your account will be immediately suspended
- Access to all widgets and services will be revoked
- We reserve the right to contest the chargeback

## Contact Us

For billing questions:
- **Email:** support@whisperai.one
- **Dashboard:** whisperai.one/dashboard/billing`,
  },
};

export default function PolicyPage() {
  const { type } = useParams();
  const policy = POLICIES[type];

  if (!policy) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="text-center">
          <p className="text-sm text-muted-foreground mb-4">Policy not found</p>
          <Link to="/" className="text-sm text-[#10B981] hover:underline">Back to home</Link>
        </div>
      </div>
    );
  }

  const renderContent = (content) => {
    return content.split('\n').map((line, i) => {
      const trimmed = line.trim();
      if (!trimmed) return <br key={i} />;
      if (trimmed.startsWith('### ')) return <h3 key={i} className="text-base font-semibold text-foreground mt-6 mb-2">{trimmed.slice(4)}</h3>;
      if (trimmed.startsWith('## ')) return <h2 key={i} className="text-lg font-bold text-foreground mt-8 mb-3">{trimmed.slice(3)}</h2>;
      if (trimmed.startsWith('# ')) return <h1 key={i} className="text-2xl font-bold text-foreground mt-4 mb-4">{trimmed.slice(2)}</h1>;
      if (trimmed.startsWith('- **')) {
        const parts = trimmed.slice(2).split('**');
        return <li key={i} className="text-sm text-muted-foreground ml-4 mb-1.5 list-disc"><strong className="text-foreground">{parts[1]}</strong>{parts[2] || ''}</li>;
      }
      if (trimmed.startsWith('- ')) return <li key={i} className="text-sm text-muted-foreground ml-4 mb-1.5 list-disc">{trimmed.slice(2)}</li>;
      if (trimmed.startsWith('**') && trimmed.endsWith('**')) return <p key={i} className="text-sm font-semibold text-foreground mb-2">{trimmed.slice(2, -2)}</p>;
      const formatted = trimmed.split(/(\*\*.*?\*\*)/g).map((part, j) => {
        if (part.startsWith('**') && part.endsWith('**')) return <strong key={j} className="text-foreground">{part.slice(2, -2)}</strong>;
        return part;
      });
      return <p key={i} className="text-sm text-muted-foreground leading-relaxed mb-2">{formatted}</p>;
    });
  };

  return (
    <div className="min-h-screen bg-background" data-testid={`${type}-policy-page`}>
      <SiteHeader />

      <main className="max-w-3xl mx-auto px-6 py-12">
        <p className="text-xs text-muted-foreground mb-2">Last updated: {policy.lastUpdated}</p>
        <article>{renderContent(policy.content)}</article>

        {/* Links to other policies */}
        <div className="mt-12 pt-6 border-t border-border">
          <p className="text-xs text-muted-foreground mb-3">Other policies:</p>
          <div className="flex flex-wrap gap-3">
            {Object.entries(POLICIES).filter(([k]) => k !== type).map(([key, val]) => (
              <Link key={key} to={`/${key}`} className="text-xs text-[#10B981] hover:underline">{val.title}</Link>
            ))}
          </div>
        </div>
      </main>

      <footer className="border-t border-border py-6 px-6 sm:px-10">
        <div className="max-w-3xl mx-auto flex items-center justify-between">
          <span className="text-xs text-muted-foreground">Whisper.AI &mdash; Powered by WPO</span>
          <Link to="/" className="text-xs text-muted-foreground hover:text-foreground">Home</Link>
        </div>
      </footer>
    </div>
  );
}
